import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button } from '@mui/material';
import {
  Groups,
  Cake,
  EmojiEvents,
  ChevronRight,
  WarningAmber,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import hrPlusService from '../../services/hrPlusService';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle, categoryAccent, MAX_SERIES } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import { ErrorState } from '../../components/common/pageKit';

// ---- pure formatters (no theme dependency) ----
const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtInt = (v: any) => (isNum(v) ? Number(v).toLocaleString('en-IN') : '—');

const fmtDate = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/** Distribution rows are the documented {label,count} shape. */
interface DistRow {
  label?: string;
  count?: number;
  [k: string]: any;
}

/**
 * Trend / analytics rows have shapes that vary by endpoint. Infer a label key
 * (first string field) and up to MAX_SERIES numeric value keys from the first
 * row, rather than assuming fixed field names.
 */
const inferKeys = (rows: any[]): { labelKey: string | null; valueKeys: string[] } => {
  if (!rows || !rows.length) return { labelKey: null, valueKeys: [] };
  const sample = rows[0] || {};
  const keys = Object.keys(sample);
  const labelKey = keys.find((k) => typeof sample[k] === 'string') || keys[0] || null;
  const valueKeys = keys.filter((k) => k !== labelKey && isNum(sample[k])).slice(0, MAX_SERIES);
  return { labelKey, valueKeys };
};

const humanize = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

const severityTone = (C: ReturnType<typeof useControlRoomColors>, severity?: string): string => {
  const s = (severity || '').toLowerCase();
  if (s.includes('high') || s.includes('critical') || s.includes('urgent')) return C.bad;
  if (s.includes('med') || s.includes('warn')) return C.warn;
  return C.sub;
};

const HrCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const T = useChartTokens();
  const accent = categoryAccent('hr');

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hrPlusService
      .commandCenter()
      .then((res: any) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load the HR command center.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};
  const attention: any[] = data?.attention || [];
  const celebrations = data?.celebrations || {};
  const birthdays: any[] = celebrations?.birthdays || [];
  const anniversaries: any[] = celebrations?.anniversaries || [];

  const hasKpi = (key: string) => Object.prototype.hasOwnProperty.call(kpis, key);

  const kpiDefs = useMemo(
    () => [
      { key: 'totalEmployees', label: 'Total Employees', fmt: fmtInt, color: C.text },
      { key: 'activeEmployees', label: 'Active Employees', fmt: fmtInt, color: C.good },
      { key: 'newJoinersThisMonth', label: 'New Joiners (MTD)', fmt: fmtInt, color: C.cyan },
      { key: 'onLeaveToday', label: 'On Leave Today', fmt: fmtInt, color: C.warn },
      { key: 'absentToday', label: 'Absent Today', fmt: fmtInt, color: C.bad },
      { key: 'lateToday', label: 'Late Today', fmt: fmtInt, color: C.warn },
      { key: 'openPositions', label: 'Open Positions', fmt: fmtInt, color: C.purple },
      { key: 'candidatesInPipeline', label: 'Candidates in Pipeline', fmt: fmtInt, color: C.cyan },
      { key: 'pendingLeaveApprovals', label: 'Pending Leave Approvals', fmt: fmtInt, color: C.warn },
      { key: 'pendingRegularizations', label: 'Pending Regularizations', fmt: fmtInt, color: C.warn },
      { key: 'payrollCostThisMonth', label: 'Payroll Cost (MTD)', fmt: fmtMoney, color: C.good },
      { key: 'attritionRatePct', label: 'Attrition Rate', fmt: fmtPct, color: C.bad },
      { key: 'headcountGrowthPct', label: 'Headcount Growth', fmt: fmtPct, color: C.good },
    ],
    [C]
  );

  const visibleKpis = kpiDefs.filter((k) => hasKpi(k.key));

  const deptData: DistRow[] = distributions.byDepartment || [];
  const locationData: DistRow[] = distributions.byLocation || [];
  const empTypeData: DistRow[] = distributions.byEmploymentType || [];
  const genderData: DistRow[] = distributions.byGender || [];

  const headcountTrend: any[] = trends.headcount || [];
  const attendanceTrend: any[] = trends.attendance || [];
  const leaveUtilTrend: any[] = trends.leaveUtilization || [];

  const headcountKeys = useMemo(() => inferKeys(headcountTrend), [headcountTrend]);
  const attendanceKeys = useMemo(() => inferKeys(attendanceTrend), [attendanceTrend]);
  const leaveUtilKeys = useMemo(() => inferKeys(leaveUtilTrend), [leaveUtilTrend]);

  const goto = (link?: string | null, fallback?: string) => {
    if (link) navigate(link);
    else if (fallback) navigate(fallback);
  };

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string; onClick?: () => void }> = ({
    label,
    value,
    color,
    onClick,
  }) => (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 2,
        p: 2,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        '&:hover': onClick ? { borderColor: C.accent } : undefined,
      }}
    >
      <Typography
        sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{
          fontWeight: 800,
          fontSize: { xs: 17, sm: 19, md: 20, lg: 22 },
          lineHeight: 1.15,
          color: color || C.text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
      {children}
    </Typography>
  );

  const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box>
      <SectionLabel>{title}</SectionLabel>
      <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, height: '100%' }}>{children}</Box>
    </Box>
  );

  const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ color: C.sub, p: 2 }}>{children}</Typography>
  );

  /** Horizontal bar for a {label,count} distribution — copes with long labels. */
  const BarDistribution: React.FC<{ rows: DistRow[]; onBarClick?: () => void }> = ({ rows, onBarClick }) => {
    if (!rows.length) return <EmptyNote>No data available.</EmptyNote>;
    return (
      <Box sx={{ width: '100%', height: Math.max(160, rows.length * 34) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
            <CartesianGrid {...gridProps(T)} horizontal={false} />
            <XAxis type="number" {...axisProps(T)} allowDecimals={false} />
            <YAxis type="category" dataKey="label" {...axisProps(T)} width={110} tick={{ fill: T.textSecondary, fontSize: 11 }} />
            <RTooltip {...tooltipStyle(T)} formatter={(v: any) => [fmtInt(v), 'Employees']} />
            <Bar dataKey="count" radius={[0, 3, 3, 0]} maxBarSize={20} onClick={onBarClick} cursor={onBarClick ? 'pointer' : undefined}>
              {rows.map((_, i) => (
                <Cell key={i} fill={T.series(i)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  /** Donut for a {label,count} distribution — best for low-cardinality splits. */
  const DonutDistribution: React.FC<{ rows: DistRow[] }> = ({ rows }) => {
    const filtered = rows.filter((r) => isNum(r.count) && Number(r.count) > 0);
    if (!filtered.length) return <EmptyNote>No data available.</EmptyNote>;
    return (
      <Box sx={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={filtered}
              dataKey="count"
              nameKey="label"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              label={(p: any) => `${p.label} (${p.count})`}
              labelLine={false}
            >
              {filtered.map((_, i) => (
                <Cell key={i} fill={T.series(i)} />
              ))}
            </Pie>
            <RTooltip {...tooltipStyle(T)} />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  /** Generic trend renderer: single numeric series -> line, multiple -> grouped bars. */
  const TrendChart: React.FC<{ rows: any[]; keys: { labelKey: string | null; valueKeys: string[] }; moneyAxis?: boolean }> = ({
    rows,
    keys,
    moneyAxis,
  }) => {
    if (!rows.length || !keys.labelKey || !keys.valueKeys.length) return <EmptyNote>No trend data available.</EmptyNote>;
    const yFmt = (v: number) => (moneyAxis ? formatIndianNumber(v) : fmtInt(v));
    if (keys.valueKeys.length === 1) {
      return (
        <Box sx={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps(T)} />
              <XAxis dataKey={keys.labelKey} {...axisProps(T)} minTickGap={20} />
              <YAxis {...axisProps(T)} width={60} tickFormatter={yFmt} allowDecimals={false} />
              <RTooltip {...tooltipStyle(T)} formatter={(v: any) => [yFmt(Number(v)), humanize(keys.valueKeys[0])]} />
              <Line type="monotone" dataKey={keys.valueKeys[0]} stroke={T.series(0)} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      );
    }
    return (
      <Box sx={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps(T)} />
            <XAxis dataKey={keys.labelKey} {...axisProps(T)} minTickGap={20} />
            <YAxis {...axisProps(T)} width={60} tickFormatter={yFmt} allowDecimals={false} />
            <RTooltip {...tooltipStyle(T)} formatter={(v: any, name: any) => [yFmt(Number(v)), humanize(String(name))]} />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} formatter={(v: string) => humanize(v)} />
            {keys.valueKeys.map((vk, i) => (
              <Bar key={vk} dataKey={vk} name={vk} fill={T.series(i)} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const celebrationName = (item: any) => item.employeeName || item.employee_name || item.name || item.fullName || item.full_name || '—';
  const celebrationSub = (item: any) => item.department || item.designation || item.department_name || '';
  const celebrationDate = (item: any) => item.date || item.dob || item.anniversaryDate || item.anniversary_date || item.eventDate;
  const celebrationYears = (item: any) => item.years ?? item.tenureYears ?? item.tenure_years ?? item.yearsOfService ?? item.years_of_service;

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${C.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Groups sx={{ color: accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>HR Command Center</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>What's happening with our workforce today?</Typography>
          </Box>
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/hr/employees')} sx={{ color: C.text, borderColor: C.line }}>
          All Employees
        </Button>
      </Box>

      {loading && <LinearProgress />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          {/* Exec KPI band */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(150px, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {visibleKpis.length ? (
              visibleKpis.map((k) => <Metric key={k.key} label={k.label} value={k.fmt(kpis[k.key])} color={k.color} />)
            ) : (
              !loading && <Typography sx={{ color: C.sub, gridColumn: '1 / -1' }}>No KPI data available.</Typography>
            )}
          </Box>

          {/* Distributions */}
          <Box sx={{ px: 3, pb: 3 }}>
            <SectionLabel>Workforce Distribution</SectionLabel>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              <Panel title="By Department">
                <BarDistribution rows={deptData} onBarClick={() => navigate('/hr/employees')} />
              </Panel>
              <Panel title="By Location">
                <BarDistribution rows={locationData} onBarClick={() => navigate('/hr/employees')} />
              </Panel>
              <Panel title="By Employment Type">
                <DonutDistribution rows={empTypeData} />
              </Panel>
              <Panel title="By Gender">
                <DonutDistribution rows={genderData} />
              </Panel>
            </Box>
          </Box>

          {/* Trends */}
          <Box sx={{ px: 3, pb: 3 }}>
            <SectionLabel>Trends</SectionLabel>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <Panel title="Headcount Trend">
                <TrendChart rows={headcountTrend} keys={headcountKeys} />
              </Panel>
              <Panel title="Attendance Trend">
                <TrendChart rows={attendanceTrend} keys={attendanceKeys} />
              </Panel>
              <Panel title="Leave Utilization">
                <TrendChart rows={leaveUtilTrend} keys={leaveUtilKeys} />
              </Panel>
            </Box>
          </Box>

          {/* Attention + Celebrations */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
            {/* HR Attention Required */}
            <Box>
              <SectionLabel>HR Attention Required</SectionLabel>
              <Stack spacing={1}>
                {attention.map((a: any, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: C.panel,
                      border: `1px solid ${C.line}`,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: severityTone(C, a.severity), flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                        {a.label || '—'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ChevronRight />}
                      disabled={!a.link}
                      onClick={() => goto(a.link)}
                      sx={{ color: C.accent, flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  </Box>
                ))}
                {!loading && !attention.length && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <WarningAmber sx={{ fontSize: 16, color: C.sub }} />
                    <Typography sx={{ color: C.sub, fontStyle: 'italic' }}>Nothing needs attention.</Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Celebrations */}
            <Box>
              <SectionLabel>Celebrations</SectionLabel>
              <Stack spacing={1.5}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                    <Cake sx={{ fontSize: 15, color: C.warn }} />
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.sub }}>Upcoming Birthdays</Typography>
                  </Stack>
                  {birthdays.length ? (
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                      {birthdays.map((b: any, i: number) => (
                        <Box
                          key={i}
                          sx={{
                            minWidth: 150,
                            flexShrink: 0,
                            bgcolor: C.panel,
                            border: `1px solid ${C.line}`,
                            borderRadius: 2,
                            p: 1.25,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: 12.5 }} noWrap>
                            {celebrationName(b)}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.sub }} noWrap>
                            {celebrationSub(b) || '—'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.warn, mt: 0.5 }}>{fmtDate(celebrationDate(b))}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    !loading && <Typography sx={{ color: C.sub, fontSize: 12, fontStyle: 'italic' }}>No birthdays coming up.</Typography>
                  )}
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                    <EmojiEvents sx={{ fontSize: 15, color: C.cyan }} />
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.sub }}>Work Anniversaries</Typography>
                  </Stack>
                  {anniversaries.length ? (
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                      {anniversaries.map((a: any, i: number) => (
                        <Box
                          key={i}
                          sx={{
                            minWidth: 150,
                            flexShrink: 0,
                            bgcolor: C.panel,
                            border: `1px solid ${C.line}`,
                            borderRadius: 2,
                            p: 1.25,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: 12.5 }} noWrap>
                            {celebrationName(a)}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.sub }} noWrap>
                            {celebrationSub(a) || '—'}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11, color: C.cyan }}>{fmtDate(celebrationDate(a))}</Typography>
                            {isNum(celebrationYears(a)) && (
                              <Typography sx={{ fontSize: 11, color: C.sub }}>{Number(celebrationYears(a))}y</Typography>
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    !loading && <Typography sx={{ color: C.sub, fontSize: 12, fontStyle: 'italic' }}>No anniversaries coming up.</Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HrCommandCenter;
